import React, { ChangeEvent, FC, KeyboardEvent, useMemo, useEffect, useRef } from "react"

import { QuestionCardInputTEXTAREA } from "./QuestionCardInput.styles"

type QuestionCardInputProps = {
  onChange: (value: string) => void,
  onFinishedEditing: (text: string) => void,
  text: string,
  textCurrent: string,
}

const QuestionCardInput: FC<QuestionCardInputProps> = ({
  onChange,
  onFinishedEditing,
  textCurrent,
}) => {
  const inputElRef = useRef<HTMLTextAreaElement | null>(null)

  // Automatically focus on the element when it's mounted
  useEffect(() => {
    if (!inputElRef.current) return

    if (inputElRef.current !== document.activeElement) {
      inputElRef.current.setSelectionRange(inputElRef.current.value.length, inputElRef.current.value.length)
      inputElRef.current.focus()
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent): void => {
      if (
        inputElRef.current &&
        inputElRef.current !== e.target &&
        !(e.target as HTMLElement).closest(".save-button")
      ) {
        onFinishedEditing(textCurrent)
      }
    }

    // If the user clicks anywhere outside of the input, finish editing!
    // This is safer than using `onBlur` on mobile devices, where the input
    // doesn't always blur when the there a mouse/touch event outside the input :/
    window.addEventListener("mousedown", handleClickOutside)
    window.addEventListener("touchstart", handleClickOutside)

    return () => {
      window.removeEventListener("mousedown", handleClickOutside)
      window.removeEventListener("touchstart", handleClickOutside)
    }
  }, [textCurrent])

  // Magically adjust the height of the textarea to match the content
  useEffect(() => {
    if (!inputElRef.current) return

    inputElRef.current.style.height = "auto"

    if (inputElRef.current.scrollHeight >= inputElRef.current.clientHeight) {
      inputElRef.current.style.height = `${inputElRef.current.scrollHeight}px`
    }
  }, [textCurrent])

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    onChange(e.target.value || "")
  }

  // If the Enter or Escape keys are pressed, exit edit mode
  // Also, remove the blur handler so we don't accidentally fire 2 actions
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (["Enter", "Escape"].includes(e.key)) {
      e.preventDefault()
      onFinishedEditing(textCurrent)
    }
  }

  return (
    <QuestionCardInputTEXTAREA
      className="QuestionCardInput"
      maxLength={240}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      ref={inputElRef}
      value={textCurrent}
    />
  )
}

export default QuestionCardInput
